import styled from 'styled-components';
import { TitleLarge500, TitleSmall500 } from '@nebula/global-styles';
import type { PlanId } from '../types';
import { commonUi } from '../../../constants';
import products from '../../../constants/products';
import { rem } from 'polished';

const planBandColor = ($plan: PlanId, theme: unknown) => {
  const colors = theme as { color: any };
  if ($plan === products.identity) {
    return colors.color.decorative.purple[90];
  }
  if ($plan === products.credit_expert) {
    return colors.color.brand.navy;
  }
  return colors.color.ui.grey[20];
};

// ── Page shell ────────────────────────────────────────────────────────────────

export const PageContainer = styled.div<{ $plan: PlanId; $webview: boolean }>`
  height: ${({ $webview }) =>
    $webview ? '100dvh' : `calc(100dvh - ${commonUi.layout.mobileHeaderAndBottomNavHeightPx}px)`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0 auto;
  position: relative;
  background: ${({ theme }) => theme.color.ui.grey[10]};
  /* background: ${({ $plan, theme }) => {
    const topColor = planBandColor($plan, theme);
    const colors = theme as { color: any };
    return `linear-gradient(to bottom, ${topColor} 18.75rem, ${colors.color.ui.grey[10]} 18.75rem)`;
  }}; */
`;

// ── Carousel viewport & track ─────────────────────────────────────────────────

export const CarouselViewport = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  touch-action: pan-y;
  overscroll-behavior-y: none;
  background: #f7f7f7;
  /* Hide scrollbar across all browsers */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/* Contains both the gradient band and the card track so they scroll as one unit. */
export const ScrollContent = styled.div`
  position: relative;
`;

/*
 * Colour band behind the hero cards — scrolls with content.
 * Uses a 3×-wide ::before pseudo-element so the ellipse arc is continuous
 * across all three plans. Shifting left by $tabIndex × -100% reveals the
 * correct coloured slice.
 */
export const GradientBand = styled.div<{ $plan: PlanId; $tabIndex: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 7.75rem;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${({ $tabIndex }) => `${$tabIndex * -100}%`};
    width: 300%;
    height: 100%;
    transition: left 300ms ease, background 300ms ease;
    background: ${({ $plan, theme }) => planBandColor($plan, theme)};
    border-bottom-left-radius: 50% 4rem;
    border-bottom-right-radius: 50% 4rem;
  }
`;

export const CarouselTrack = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  will-change: transform;
`;

export const CardGap = styled.div`
  width: 1rem;
  flex-shrink: 0;
`;

export const PlanPanelWrapper = styled.div`
  width: calc(100vw - 3.5rem);
  flex-shrink: 0;
`;

// ── Page title ────────────────────────────────────────────────────────────────

export const TitleArea = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? '7.5rem' : '0')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: max-height 250ms ease, opacity 200ms ease;
  background: ${({ theme }) => (theme as any).color.ui.grey[10]};
  padding: ${({ $visible }) => ($visible ? '1rem' : '0 1.25rem')};
`;

export const PageTitle = styled(TitleLarge500)`
  margin: 0;
  padding-top: 0.5rem;
  color: ${({ theme }) => (theme as any).color.ui.grey[100]};
`;

export const MobileErrorAlertArea = styled.div<{ $visible: boolean; $webview: boolean }>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  max-height: ${({ $visible }) => ($visible ? '24rem' : '0')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: max-height 250ms ease, opacity 200ms ease;
  background: ${({ theme }) => (theme as any).color.ui.grey[10]};
  padding: ${({ $webview, $visible }) => {
    if (!$visible) {
      return '0';
    }
    if ($webview) {
      return `${rem(16)} ${rem(16)}`;
    }
    return `0 ${rem(16)} ${rem(16)}`;
  }};
`;

export const MobileErrorAlertContainer = styled.div.attrs({ 'data-track-view': true })``;

// ── Sticky plan header ──────────────────────────────────────────────────────────

export const StickyHeader = styled.div<{ $plan: PlanId; $visible: boolean; $webview: boolean }>`
  flex-shrink: 0;
  height: 2.75rem;
  max-height: ${({ $visible }) => ($visible ? '2.75rem' : '0')};
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: max-height 250ms ease, opacity 200ms ease;
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  background: ${({ $plan, theme }) => planBandColor($plan, theme)};
`;

export const SegmentedControlArea = styled.div<{ $visible: boolean }>`
  background: ${({ $plan, theme }) => planBandColor($plan, theme)};
  flex-shrink: 0;
  max-height: ${({ $visible }) => ($visible ? '5rem' : '0')};
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: max-height 250ms ease, opacity 200ms ease;
`;

// ── Sticky plan header title ────────────────────────────────────────────────────

export const StickyHeaderTitle = styled(TitleSmall500)<{ $plan: PlanId }>`
  margin: 0;
  color: ${({ $plan, theme }) =>
    $plan === products.basic ? (theme as any).color.ui.grey[100] : (theme as any).color.brand.white};
`;

// ── Sticky footer ─────────────────────────────────────────────────────────────

export const Fade = styled.div`
  flex-shrink: 0;
  height: 0.75rem;
  background: linear-gradient(180deg, rgba(226, 228, 231, 0) 0%, rgba(226, 228, 231, 0.6) 100%);
  pointer-events: none;
  z-index: 10;
  position: relative;
`;
